import React from 'react';
import {
  Text, Table, TextInput, Select, Checkbox, Button, Group, Stack
} from '@mantine/core';
import { IconPlus, IconTrash } from '@tabler/icons-react';

const AGENT_TYPES = [
  { value: 'autonomous', label: 'Autonomous (LLM-driven router)' },
  { value: 'scripted',   label: 'Scripted (slot-filling chain)' }
];

const ROLES = [
  { value: 'bot',     label: 'Bot' },
  { value: 'context', label: 'Context / overview (skip)' },
  { value: 'misc',    label: 'Misc (skip)' }
];

/**
 * Step 2 — page roster review.
 *
 * v1: the user enters the roster manually (one row per bot page). The
 * Bedrock classifier integration is a v2 enhancement — wiring it in
 * requires a pre-generation classify call, which lengthens the wizard.
 * For now the user inputs:
 *   - index         (PDF page number)
 *   - suggestedName (bot display name)
 *   - agentType     (autonomous / scripted)
 *   - exitsToBots   (comma-separated list of downstream bot names)
 *   - brief         (one-sentence purpose)
 */
function DiagramPageReviewStep({ roster, onChange }) {
  const addRow = () => {
    onChange([
      ...roster,
      {
        index: (roster[roster.length - 1]?.index || 0) + 1,
        role: 'bot',
        suggestedName: '',
        agentType: 'scripted',
        exitsToBots: [],
        brief: '',
        skip: false
      }
    ]);
  };

  const updateRow = (i, patch) => {
    onChange(roster.map((r, ix) => (ix === i ? { ...r, ...patch } : r)));
  };

  const removeRow = (i) => {
    onChange(roster.filter((_, ix) => ix !== i));
  };

  return (
    <Stack gap="md">
      <Text size="sm" c="dimmed">
        Add a row for every page of the PDF. Mark "context" or "misc" for non-bot pages and they'll be skipped.
      </Text>

      {roster.length > 0 && (
        <Table striped withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th style={{ width: 70 }}>Page</Table.Th>
              <Table.Th>Bot name</Table.Th>
              <Table.Th>Role</Table.Th>
              <Table.Th>Agent type</Table.Th>
              <Table.Th>Exits to (comma-separated)</Table.Th>
              <Table.Th>Brief</Table.Th>
              <Table.Th style={{ width: 80 }}>Skip</Table.Th>
              <Table.Th style={{ width: 40 }}></Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {roster.map((row, i) => (
              <Table.Tr key={i}>
                <Table.Td>
                  <TextInput
                    type="number"
                    size="xs"
                    value={row.index}
                    onChange={(e) => updateRow(i, { index: Number(e.target.value) })}
                  />
                </Table.Td>
                <Table.Td>
                  <TextInput
                    size="xs"
                    placeholder="OrthoMain"
                    value={row.suggestedName}
                    onChange={(e) => updateRow(i, { suggestedName: e.target.value })}
                  />
                </Table.Td>
                <Table.Td>
                  <Select
                    size="xs"
                    data={ROLES}
                    value={row.role}
                    onChange={(value) => updateRow(i, { role: value })}
                  />
                </Table.Td>
                <Table.Td>
                  <Select
                    size="xs"
                    data={AGENT_TYPES}
                    value={row.agentType}
                    onChange={(value) => updateRow(i, { agentType: value })}
                    disabled={row.role !== 'bot'}
                  />
                </Table.Td>
                <Table.Td>
                  <TextInput
                    size="xs"
                    placeholder="PatientType, PatientRegistration"
                    value={(row.exitsToBots || []).join(', ')}
                    onChange={(e) => updateRow(i, {
                      exitsToBots: e.target.value
                        .split(',')
                        .map((s) => s.trim())
                        .filter(Boolean)
                    })}
                  />
                </Table.Td>
                <Table.Td>
                  <TextInput
                    size="xs"
                    placeholder="Entry router…"
                    value={row.brief}
                    onChange={(e) => updateRow(i, { brief: e.target.value })}
                  />
                </Table.Td>
                <Table.Td>
                  <Checkbox
                    checked={row.skip}
                    onChange={(e) => updateRow(i, { skip: e.currentTarget.checked })}
                  />
                </Table.Td>
                <Table.Td>
                  <Button variant="subtle" color="red" size="xs" px={6} onClick={() => removeRow(i)}>
                    <IconTrash size={14} />
                  </Button>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}

      <Group>
        <Button leftSection={<IconPlus size={14} />} variant="light" color="cyan" onClick={addRow}>
          Add page
        </Button>
        {roster.length === 0 && (
          <Text size="sm" c="dimmed">Add at least one bot page to continue.</Text>
        )}
      </Group>
    </Stack>
  );
}

export default DiagramPageReviewStep;
